/** Android application id of the Mentor Me student app. */
export const mobileAppPackage = "io.mentoor.student";

/** Custom URL scheme registered by the Mentor Me student app. */
export const mobileAppScheme = "mentorme";

/** Landing page that explains the app download for a platform (desktop / iOS / shared links). */
export const mobileAppDownloadPageUrl = "https://me.mentoor.io/download";

/** Google Play listing of the student app. */
export const mobileAppPlayStoreUrl = `https://play.google.com/store/apps/details?id=${mobileAppPackage}`;

/**
 * Tenant identity carried to the app so it can pre-select this platform.
 * No user data is ever included.
 */
export type MobileAppPlatformTarget = {
  clientId: number;
  domain: string;
};

/**
 * Normalize the current student-site hostname into the canonical tenant
 * domain (lowercase, without `www.` or a trailing dot).
 *
 * @example
 * normalizeMobileAppDomain("WWW.Shipria-Academy.com"); // "shipria-academy.com"
 */
export function normalizeMobileAppDomain(hostname: string) {
  return hostname
    .trim()
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/\.$/, "");
}

/**
 * Detect a regular Android browser. The app's own WebView (`wv`) and any
 * screen rendered inside the Mentor Me app (`MentorMeMobile`) are excluded,
 * as are desktop and iOS browsers.
 *
 * @example
 * isAndroidBrowserUserAgent(navigator.userAgent);
 */
export function isAndroidBrowserUserAgent(userAgent: string) {
  if (!/Android/i.test(userAgent)) return false;
  if (/\bwv\b/.test(userAgent)) return false;
  if (/MentorMeMobile/i.test(userAgent)) return false;

  return true;
}

/**
 * Build the raw Google Play install referrer for one platform. The app reads
 * it on first launch (`parseInstallReferrer` in mobile-app
 * `src/domain/platform-hint.ts`) and opens this platform directly. Same format
 * as the landing page (`landing-page/src/shared/mobile-app.ts`).
 *
 * @example
 * buildMobileAppInstallReferrer({ clientId: 238698, domain: "mentor-me.mentoor.io" });
 * // "utm_source=web&utm_medium=banner&client_id=238698&domain=mentor-me.mentoor.io"
 */
export function buildMobileAppInstallReferrer({
  clientId,
  domain,
}: MobileAppPlatformTarget) {
  return (
    "utm_source=web&utm_medium=banner" +
    `&client_id=${encodeURIComponent(clientId)}` +
    `&domain=${encodeURIComponent(domain)}`
  );
}

/**
 * Build the Google Play listing link with this platform in the install
 * referrer (the whole referrer is one URL-encoded value).
 *
 * @example
 * buildMobileAppPlayStoreUrl({ clientId: 238698, domain: "mentor-me.mentoor.io" });
 * // https://play.google.com/store/apps/details?id=io.mentoor.student&referrer=utm_source%3Dweb%26utm_medium%3Dbanner%26client_id%3D238698%26domain%3Dmentor-me.mentoor.io
 */
export function buildMobileAppPlayStoreUrl(target: MobileAppPlatformTarget) {
  return `${mobileAppPlayStoreUrl}&referrer=${encodeURIComponent(buildMobileAppInstallReferrer(target))}`;
}

/**
 * Build the attributed download page link for one platform. Used where the
 * visitor is not on Android (the page explains the app and links to Play).
 *
 * @example
 * buildMobileAppInstallUrl({ clientId: 12, domain: "shipria-academy.com" });
 * // https://me.mentoor.io/download?client_id=12&domain=shipria-academy.com&utm_source=web&utm_medium=banner
 */
export function buildMobileAppInstallUrl({
  clientId,
  domain,
}: MobileAppPlatformTarget) {
  return (
    `${mobileAppDownloadPageUrl}?client_id=${encodeURIComponent(clientId)}` +
    `&domain=${encodeURIComponent(domain)}` +
    "&utm_source=web&utm_medium=banner"
  );
}

/**
 * Build the Android intent link: opens the installed app on this platform;
 * when the app is missing, the browser follows the fallback straight to the
 * Play listing with this platform in the install referrer, so the first
 * launch after install opens it (one tap from the teacher's site to Play).
 * Only used on Android browsers (see `isAndroidBrowserUserAgent`).
 *
 * @example
 * buildMobileAppIntentUrl({ clientId: 238698, domain: "mentor-me.mentoor.io" });
 * // intent://open?client_id=238698&domain=mentor-me.mentoor.io#Intent;scheme=mentorme;package=io.mentoor.student;S.browser_fallback_url=<encoded Play URL>;end
 */
export function buildMobileAppIntentUrl(target: MobileAppPlatformTarget) {
  return (
    `intent://open?client_id=${encodeURIComponent(target.clientId)}` +
    `&domain=${encodeURIComponent(target.domain)}` +
    `#Intent;scheme=${mobileAppScheme};package=${mobileAppPackage};` +
    `S.browser_fallback_url=${encodeURIComponent(buildMobileAppPlayStoreUrl(target))};end`
  );
}
